import React, { useContext, useState } from 'react';
import { Alert, Button, Empty, Space, Spin, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../App';
import { useCC } from '../../contexts/CCContext';
import CCControls from '../crop-intelligence/CCControls';
import { classForCode, classLabel, polygonAreaHa } from '../crop-intelligence/contract';
import useCCStats from '../crop-intelligence/useCCStats';
import CCDashboard from '../crop-intelligence/CCDashboard';

export default function CropClassificationStats() {
  const cc = useCC();
  if (cc.transport === 'v2') return <CCDashboard />;
  return <LegacyCropClassificationStats />;
}

function LegacyCropClassificationStats() {
  const { globalSelectedRegion, setInvestigatePolygon, setGlobalCCViewEnabled } = useContext(GlobalContext);
  const { dataset, country, metadata, selectedPolygon, retry } = useCC();
  const { t, i18n } = useTranslation('translation');
  const navigate = useNavigate();
  const [revision, setRevision] = useState(0);
  const result = useCCStats(dataset, country, globalSelectedRegion?.properties?.key, revision);
  const format = (value) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const columns = [
    { title: t('cc.class'), dataIndex: 'crop', render: (code) => {
      const entry = classForCode(metadata, code);
      return <span><i className="cc-swatch" style={{ backgroundColor: entry.fill, borderColor: entry.stroke }} />{classLabel(metadata, code, t)}</span>;
    } },
    { title: t('cc.area'), dataIndex: 'crop_area_ha', render: (value) => `${format(value)} ha` },
    { title: t('cc.share'), dataIndex: 'crop_percentage', render: (value) => `${format(value)}%` },
  ];
  return <div className="cc-stats">
    <CCControls />
    {selectedPolygon && dataset && <Space direction="vertical" className="cc-selection">
      <Typography.Text strong>{classLabel(metadata, selectedPolygon.properties.crop_code, t)} · {dataset.year}</Typography.Text>
      <Typography.Text>{t('cc.polygonArea')}: {format(polygonAreaHa(selectedPolygon))} ha</Typography.Text>
      <Button onClick={() => {
        setInvestigatePolygon(selectedPolygon);
        setGlobalCCViewEnabled(false);
        navigate('/investigate');
      }}>{t('cc.investigate')}</Button>
    </Space>}
    {dataset && <>
      <Typography.Title level={5}>{globalSelectedRegion?.properties?.name || t('general.region_not_selected')} · {dataset.year}</Typography.Title>
      {result.status === 'idle' && <Empty description={t('general.region_not_selected')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {result.status === 'loading' && <Spin aria-label={t('cc.loading')} />}
      {result.status === 'error' && <Alert type="error" showIcon message={t(result.code === 409 ? 'cc.datasetNotReady' : result.code === 404 ? 'cc.regionUnavailable' : 'cc.statsError')}
        action={<Button onClick={() => { if (result.code === 409) retry(); else setRevision((value) => value + 1); }}>{t('cc.retry')}</Button>} />}
      {result.status === 'ready' && <>
        <p>{t('cc.totalArea')}: <strong>{format(result.data.total_area_ha)} ha</strong><br />
          {t('cc.agriculturalArea')}: <strong>{format(result.data.agricultural_area_ha)} ha</strong></p>
        {result.data.stats.length ? <Table rowKey="crop" size="small" pagination={false} columns={columns}
          dataSource={result.data.stats} scroll={{ x: 310 }} /> : <Empty description={t('cc.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        <Typography.Text type="secondary">{t('cc.areaExplanation')}</Typography.Text>
      </>}
    </>}
  </div>;
}
